import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog"; 
import { useLanguage } from "context/LanguageContext";
import { useNotification } from "context/NotificationContext"; 
import { InputText } from "primereact/inputtext";
import { handleNavigateBack } from 'components/formUtils';  

const IdentificacaoForm = () => {
  const { GetTranslation } = useLanguage();
  const notify = useNotification(); 
  const navigate = useNavigate();
  const { id } = useParams(); // Obter o id da identificação, se estiver editando

  const [formData, setFormData] = useState({
    id_paciente: "",
    instituicao_id: null, // Valor inicial para dropdown
    nome: "",
    cpf: ""
  });

  const [institutions, setInstitutions] = useState([]); // Para armazenar as instituições do usuário

  const token = localStorage.getItem("token");

  // Carregar os dados de uma identificação existente e as instituições associadas
  useEffect(() => {
    if (id) {
      // Busca a identificação pelo ID, se estiver editando
      fetch(`${process.env.REACT_APP_API_URL}/identificacao/paciente/info/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            setFormData(data.paciente); // Preenche o formulário com os dados recebidos
          } else {
            notify({
              type: "error",
              message: data.error || "Erro ao buscar identificação"
            });
          }
        })
        .catch(() => {
          notify({
            type: "error",
            message: "Erro ao buscar identificação"
          });
        });
    }

    // Buscar as instituições vinculadas ao usuário logado
    fetch(`${process.env.REACT_APP_API_URL}/institution/user_institutions`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      }
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setInstitutions(data.institutions); // Define a lista de instituições
        } else {
          notify({
            type: "error",
            message: "Erro ao buscar instituições"
          });
        }
      })
      .catch(() => {
        notify({
          type: "error",
          message: "Erro ao carregar instituições"
        });
      });
  }, [id, token, notify]);

  const validateFields = () => {
    const missingFields = [];
    if (!formData.id_paciente) missingFields.push(GetTranslation("ID_Paciente"));
    if (!formData.instituicao_id) missingFields.push(GetTranslation("Instituicao_ID"));
    if (!formData.nome) missingFields.push(GetTranslation("Nome"));
    if (!formData.cpf) missingFields.push(GetTranslation("CPF"));

    if (missingFields.length > 0) {
      notify({
        type: "error",
        message: `Os seguintes campos são obrigatórios: ${missingFields.join(", ")}`
      });
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateFields()) return;

    const apiUrl = id
      ? `${process.env.REACT_APP_API_URL}/identificacao/update/${id}`
      : `${process.env.REACT_APP_API_URL}/identificacao/add`;

    const method = id ? "PUT" : "POST";

    fetch(apiUrl, {
      method: method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(formData)
    })
      .then((response) => {
        if (response.ok) {
          notify({
            type: "success",
            message: id ? "Identificação atualizada com sucesso!" : "Identificação adicionada com sucesso!"
          });
        } else {
          response.json().then((data) => {
            notify({
              type: "error",
              message: `Erro: ${data.error}`
            });
          });
        }
      })
      .catch(() => {
        notify({
          type: "error",
          message: "Erro ao enviar os dados"
        });
      });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleInstitutionChange = (e) => {
    setFormData({ ...formData, instituicao_id: e.target.value });
  };

  // Modal de confirmação para salvar os dados
  const confirmSave = (e) => {
    e.preventDefault(); // Evita o reload da página
    confirmDialog({
      message: GetTranslation("Confirmar_salvar"),
      header: GetTranslation("Confirmar"),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: GetTranslation("Yes"),  // Botão de aceitar com tradução
      rejectLabel: GetTranslation("No"),   // Botão de rejeitar com tradução
      accept: handleSubmit, // Salva ao aceitar
      reject: () => {
        notify({
          type: "info",
          message: GetTranslation("Operacao_cancelada")
        });
      }
    });
  };

  const handleBack = () => {
    if (id)
      navigate("/app/categoria/" + id);
    else 
      navigate("/app/home/");
  };

  return (
    <div className="card p-3">
      <ConfirmDialog /> 
      <h4>{id ? GetTranslation("Identificacao") : GetTranslation("Identificacao")}</h4>
      <form className="row g-3" onSubmit={confirmSave}>
        <div className="col-md-6">
          <label className="form-label">{GetTranslation("ID_Paciente")}</label>
          <InputText
            className="form-control"
            name="id_paciente"
            value={formData.id_paciente}
            onChange={handleChange}
            required
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">{GetTranslation("Instituicao_ID")}</label>
          <select
            className="form-select"
            value={formData.instituicao_id}
            onChange={handleInstitutionChange}
            required
          >
            <option value="">{GetTranslation("Select_Instituicao")}</option>
            {institutions.map((institution) => (
              <option key={institution.institution_id} value={institution.institution_id}>
                {institution.institution_name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label">{GetTranslation("Nome")}</label>
          <InputText
            className="form-control"
            name="nome"
            value={formData.nome}
            onChange={handleChange}
            required
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">{GetTranslation("CPF")}</label>
          <InputText
            className="form-control"
            name="cpf"
            value={formData.cpf}
            onChange={handleChange}
            required
          />
        </div>
        <div className="col-12 d-flex justify-content-between">
          {/* Botão de voltar */}
          <Button
            label={GetTranslation("Voltar")}
            className="btn btn-secondary"
            onClick={handleBack} // Função para voltar
          />
          {/* Botão de salvar/criar */}
          <Button
            label={id ? GetTranslation("Update") : GetTranslation("Create")}
            className="btn btn-primary"
            type="submit" // Define como botão de submit
          />
        </div>
      </form>
    </div>
  );
};

export default IdentificacaoForm;
